import type { PaymentMethodsSource } from '@/app/config/products.config';
import type { BadgeTone } from '@/shared/ui';
import type {
  ProductPaymentSettingsList,
  ProductPaymentSettingsSummary,
} from '@/services';
import { FINANCE_METHOD_LABELS } from '@/shared/utils/finance-method-label.util';
import { formatDateTimeShortToBrazil } from '@/shared/utils/format-date.util';
import {
  CARD_MODE_LABELS,
  EXTERNAL_PAYMENT_SOURCE_INFO,
  PIX_MODE_LABELS,
} from './payment-settings-labels.util';

export type PaymentSettingsRow = {
  productCode: string;
  name: string;
  status: { label: string; tone: BadgeTone };
  /** Formas ligadas com a variação de cada uma (ex.: "PIX Automático", "Cartão · one shot"). */
  methods: string[];
  catalogSummary: string;
  /** Nota de pacote de parceiro ("usa também o catálogo do 140001"). */
  inheritsNote?: string;
  updatedNote?: string;
  /** Ausente quando as formas do produto não são configuradas nesta tela. */
  actionLabel?: string;
};

/**
 * O que o `products.config.ts` diz de cada produto: o nome, como o resto das
 * Configurações mostra (o do BFF é o reserva), e onde ficam as formas de
 * pagamento.
 */
export type ProductDescriptors = Record<
  string,
  { name: string; paymentMethodsSource: PaymentMethodsSource }
>;

/** Situação da cobrança pelo gateway, para a etiqueta da lista e do editor. */
export function gatewayStatusOf(gateway: {
  gatewaySupported: boolean;
  gatewayActive: boolean;
  gatewayEnabled: boolean;
}): PaymentSettingsRow['status'] {
  if (gateway.gatewayActive)
    return { label: 'Gateway ligado', tone: 'success' };
  if (gateway.gatewayEnabled)
    return { label: 'Gateway ligado com erro de configuração', tone: 'error' };
  if (!gateway.gatewaySupported)
    return { label: 'Gateway indisponível neste produto', tone: 'gray' };
  return { label: 'Gateway desligado', tone: 'warning' };
}

function methodsOf(item: ProductPaymentSettingsSummary): string[] {
  if (!item.gatewayEnabled) return [];
  return item.enabledMethods.map(method => {
    if (method === 'PIX') return PIX_MODE_LABELS[item.pixBillingMode];
    if (method === 'CREDIT_CARD')
      return `Cartão · ${CARD_MODE_LABELS[item.cardBillingMode].toLowerCase()}`;
    return FINANCE_METHOD_LABELS[method];
  });
}

function catalogSummaryOf(item: ProductPaymentSettingsSummary): string {
  if (item.totalMethodsCount === undefined)
    return 'Catálogo indisponível no momento';
  if (item.totalMethodsCount === 0) return 'Sem formas cadastradas';
  const total = `${item.totalMethodsCount} ${item.totalMethodsCount === 1 ? 'forma' : 'formas'}`;
  return `${total} no catálogo · ${item.activeMethodsCount ?? 0} ativa(s)`;
}

/**
 * Linhas da lista: produtos que aceitam o gateway primeiro, depois os demais e,
 * por último, os que definem as formas fora do Portal — cada grupo em ordem
 * alfabética. Produto que existe só no `products.config.ts` (sem config nem
 * catálogo no BFF) entra como "sem formas cadastradas".
 */
export function buildPaymentSettingsRows(
  list: ProductPaymentSettingsList,
  products: ProductDescriptors
): PaymentSettingsRow[] {
  const items = [...list.items];
  const listedCodes = new Set(items.map(item => item.productCode));
  const catalogReadable = items.some(
    item => item.totalMethodsCount !== undefined
  );

  for (const productCode of Object.keys(products)) {
    if (listedCodes.has(productCode)) continue;
    items.push({
      productCode,
      productLabel: products[productCode].name,
      gatewaySupported: false,
      gatewayActive: false,
      gatewayEnabled: false,
      enabledMethods: [],
      cardBillingMode: 'PER_INSTALLMENT',
      pixBillingMode: 'SCHEDULED',
      activeMethodsCount: catalogReadable ? 0 : undefined,
      totalMethodsCount: catalogReadable ? 0 : undefined,
      validationErrors: [],
    });
  }

  const sourceOf = (productCode: string): PaymentMethodsSource =>
    products[productCode]?.paymentMethodsSource ?? 'portal';

  const toRow = (item: ProductPaymentSettingsSummary): PaymentSettingsRow => {
    const name = products[item.productCode]?.name ?? item.productLabel;
    const source = sourceOf(item.productCode);
    if (source !== 'portal') {
      const external = EXTERNAL_PAYMENT_SOURCE_INFO[source];
      return {
        productCode: item.productCode,
        name,
        status: { label: external.label, tone: 'gray' },
        methods: [],
        catalogSummary: external.description,
      };
    }

    const updatedAt = formatDateTimeShortToBrazil(item.updatedAt);
    return {
      productCode: item.productCode,
      name,
      status: gatewayStatusOf(item),
      methods: methodsOf(item),
      catalogSummary: catalogSummaryOf(item),
      inheritsNote: item.catalogInheritsFrom
        ? `Usa também o catálogo do ${item.catalogInheritsFrom}`
        : undefined,
      updatedNote:
        item.updatedByName && updatedAt
          ? `Alterado por ${item.updatedByName} em ${updatedAt}`
          : undefined,
      actionLabel: item.gatewaySupported ? 'Configurar' : 'Ver catálogo',
    };
  };

  const groupOf = (item: ProductPaymentSettingsSummary): number => {
    if (sourceOf(item.productCode) !== 'portal') return 2;
    return item.gatewaySupported ? 0 : 1;
  };

  return items
    .map(item => ({ group: groupOf(item), row: toRow(item) }))
    .sort(
      (a, b) =>
        a.group - b.group || a.row.name.localeCompare(b.row.name, 'pt-BR')
    )
    .map(entry => entry.row);
}
